import { get } from 'svelte/store'
import { createMutation, createQuery } from '@tanstack/svelte-query'
import { storeScene } from '../../../stores/scene'
import type { Group, Object3D } from 'three'
import { getObjectsByProperties } from '../../../helpers/scene-json'
import { ThreeItemKeys, ThreeItemTypes } from '../../../constants/three'
import queryClient from '../../../constants/query-client'
import { canvasSelection, pruneMissingFromMultiSelection } from '../../../stores/canvas-selection'
import { restoreOriginalMaterial } from '../../../helpers/selection-material'
import { recenterGroupOnChildren } from '../../../helpers/three/recenter-group'

const useObjectsQueries = () => {
  const getObjects = async () => {
    const objects: Object3D[] = []

    const scene = get(storeScene)
    scene?.traverse((child) => {
      try {
        objects.push(child)
      }
      catch (err) {
        console.error(err)
      }
    })
    return objects
  }

  const getObject = (objectUuid?: Object3D['uuid']) => {
    const foundObject = get(storeScene)?.getObjectByProperty('uuid', objectUuid)

    if (!foundObject) {
      throw new Error('object not found')
    }

    return foundObject
  }

  const getFiles = async () => {
    const scene = get(storeScene)
    if (!scene) return []

    return getObjectsByProperties(scene, (object) => {
      return object.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
    })
  }

  const useFiles = () => createQuery(() => ({
    queryKey: ['canvas', 'files'],
    queryFn: getFiles,
  }), () => queryClient)

  const invalidateCanvas = () => {
    queryClient.invalidateQueries({ queryKey: ['canvas'] })
  }

  const useObjects = () => createQuery(() => ({
    queryKey: ['canvas', 'objects'],
    queryFn: getObjects,
  }), () => queryClient)

  const removeObjectMutation = createMutation(() => ({
    mutationFn: (objectUuid: Object3D['uuid']) => new Promise((resolve) => {
      const object = getObject(objectUuid)
      const selectedElement = get(canvasSelection)

      if (selectedElement?.uuid === objectUuid) {
        restoreOriginalMaterial(selectedElement)
        canvasSelection.set(undefined)
      }

      if (object.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.PRINTER) {
        const printerId = object.userData[ThreeItemKeys.PRINTER_ID]
        if (printerId) {
          get(storeScene)?.traverse((item) => {
            if (item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId) {
              delete item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID]
              delete item.userData[ThreeItemKeys.PRINTING_ORDER]
              item.traverse((child) => {
                if ('material' in child && !child.userData[ThreeItemKeys.TRANSIENT]) {
                  const mat = child.material as { color?: { set: (c: string) => void }, toJSON?: () => unknown }
                  mat.color?.set('lightgray')
                  child.userData.originalMaterial = mat.toJSON?.()
                }
              })
            }
          })
        }
      }

      let parentGroup: Group | undefined
      if (
        object.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
        && object.userData[ThreeItemKeys.ASSIGNED_GROUP_ID]
      ) {
        object.traverseAncestors((ancestor) => {
          if (
            !parentGroup
            && ancestor.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP
            && ancestor.userData[ThreeItemKeys.GROUP_ID] === object.userData[ThreeItemKeys.ASSIGNED_GROUP_ID]
          ) {
            parentGroup = ancestor as Group
          }
        })
      }

      object?.removeFromParent()

      if (parentGroup) {
        if (parentGroup.children.length === 0) {
          parentGroup.removeFromParent()
        }
        else {
          recenterGroupOnChildren(parentGroup)
        }
      }

      resolve(true)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      invalidateCanvas()
    },
  }), () => queryClient)

  return {
    useObjects,
    useFiles,
    invalidateCanvas,
    removeObjectMutation,
  }
}

export default useObjectsQueries
